import type { ReactNode } from 'react'

export interface MatchRange {
	start: number
	end: number
}

/**
 * Split text by match ranges and return React nodes with highlighted marks.
 * Current match uses purple tint; other matches use yellow.
 */
export function renderHighlightedText(
	text: string,
	ranges: MatchRange[],
	currentMatchIndex: number
): ReactNode {
	if (ranges.length === 0) return text

	const parts: Array<{
		text: string
		isMatch: boolean
		isCurrent: boolean
		start?: number
		end?: number
	}> = []
	let lastEnd = 0

	for (let i = 0; i < ranges.length; i++) {
		const r = ranges[i]
		if (r.start > lastEnd) {
			parts.push({
				text: text.slice(lastEnd, r.start),
				isMatch: false,
				isCurrent: false,
			})
		}
		parts.push({
			text: text.slice(r.start, r.end),
			isMatch: true,
			isCurrent: i === currentMatchIndex,
			start: r.start,
			end: r.end,
		})
		lastEnd = r.end
	}
	if (lastEnd < text.length) {
		parts.push({
			text: text.slice(lastEnd),
			isMatch: false,
			isCurrent: false,
		})
	}

	return parts.map((p, i) => {
		const key =
			p.isMatch && p.start != null && p.end != null
				? `m-${p.start}-${p.end}`
				: `t-${i}-${p.text.length}`
		return p.isMatch ? (
			<mark
				key={key}
				className={
					p.isCurrent
						? 'bg-[#8c52ff]/40 rounded px-0.5'
						: 'bg-yellow-200 dark:bg-yellow-900/50 rounded px-0.5'
				}
				data-current-match={p.isCurrent ? 'true' : undefined}
			>
				{p.text}
			</mark>
		) : (
			<span key={key}>{p.text}</span>
		)
	})
}
